const AUDIO_QUEUE_DB = 'dataunfold-audio-cache';
const AUDIO_QUEUE_STORE = 'recordings';
const AUDIO_QUEUE_INDEX = 'caseId';
const AUDIO_QUEUE_VERSION = 2;

let audioQueueDbPromise = null;
let audioQueueDrainLocked = false;

const getAudioQueueDb = () => {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('indexedDB unavailable'));
  }

  if (!audioQueueDbPromise) {
    audioQueueDbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(AUDIO_QUEUE_DB, AUDIO_QUEUE_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        let store = null;

        if (!db.objectStoreNames.contains(AUDIO_QUEUE_STORE)) {
          store = db.createObjectStore(AUDIO_QUEUE_STORE, { keyPath: 'key' });
        } else {
          store = request.transaction.objectStore(AUDIO_QUEUE_STORE);
        }

        if (store && !store.indexNames.contains(AUDIO_QUEUE_INDEX)) {
          store.createIndex(AUDIO_QUEUE_INDEX, AUDIO_QUEUE_INDEX, { unique: false });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  return audioQueueDbPromise;
};

const normalizeRecord = (record) => {
  if (!record) return record;
  if (!record.caseId && typeof record.key === 'string' && record.key.startsWith('case:')) {
    return { ...record, caseId: record.key };
  }
  return record;
};

export const generateRecordingKey = (caseIdKey) => {
  const randomPart = Math.random().toString(36).slice(2, 8);
  return `${caseIdKey || 'case:unknown'}:${Date.now()}-${randomPart}`;
};

export const getCaseIdFromKey = (caseIdKey) => {
  if (!caseIdKey) return '';
  if (typeof caseIdKey === 'string' && caseIdKey.startsWith('case:')) {
    return caseIdKey.slice(5);
  }
  return String(caseIdKey);
};

export const saveAudioRecording = async (record) => {
  const db = await getAudioQueueDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(AUDIO_QUEUE_STORE, 'readwrite');
    tx.oncomplete = () => resolve(record);
    tx.onerror = () => reject(tx.error);
    tx.objectStore(AUDIO_QUEUE_STORE).put(record);
  });
};

export const loadCaseRecordings = async (caseId) => {
  const db = await getAudioQueueDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(AUDIO_QUEUE_STORE, 'readonly');
    const store = tx.objectStore(AUDIO_QUEUE_STORE);
    const request = store.indexNames.contains(AUDIO_QUEUE_INDEX)
      ? store.index(AUDIO_QUEUE_INDEX).getAll(caseId)
      : store.getAll();
    request.onsuccess = () => {
      const results = (request.result || [])
        .map(normalizeRecord)
        .filter((record) => record?.caseId === caseId);
      results.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      resolve(results);
    };
    request.onerror = () => reject(request.error);
  });
};

export const loadPendingCaseRecordings = async (caseId) => {
  const records = await loadCaseRecordings(caseId);
  return records
    .filter((record) => record?.pendingUpload)
    .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
};

export const loadAllPendingRecordings = async () => {
  const db = await getAudioQueueDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(AUDIO_QUEUE_STORE, 'readonly');
    const store = tx.objectStore(AUDIO_QUEUE_STORE);
    const request = store.getAll();
    request.onsuccess = () => {
      const results = (request.result || [])
        .map(normalizeRecord)
        .filter((record) => record?.pendingUpload)
        .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
      resolve(results);
    };
    request.onerror = () => reject(request.error);
  });
};

export const deleteRecording = async (key) => {
  if (!key) return true;
  const db = await getAudioQueueDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(AUDIO_QUEUE_STORE, 'readwrite');
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
    tx.objectStore(AUDIO_QUEUE_STORE).delete(key);
  });
};

export const deleteRecordings = async (keys) => {
  if (!Array.isArray(keys) || keys.length === 0) return true;
  const db = await getAudioQueueDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(AUDIO_QUEUE_STORE, 'readwrite');
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
    const store = tx.objectStore(AUDIO_QUEUE_STORE);
    keys.forEach((key) => {
      store.delete(key);
    });
  });
};

export const acquireAudioQueueLock = () => {
  if (audioQueueDrainLocked) return false;
  audioQueueDrainLocked = true;
  return true;
};

export const releaseAudioQueueLock = () => {
  audioQueueDrainLocked = false;
};
